import { Cookie, getCookieValue, setCookieValue } from '@tmh-website/services/cookie.service';
import { use, useContext, useEffect, useState } from 'react';
import { AnyPage, getDefaultAudience } from '../cms';
import { Audience, LocalePrefix, LocaleToAudienceMap, Project, getFavoredAudience } from '../const';
import { ProjectContext } from '../contexts/project.context';

/**
 * Automatically picks the relevant audience based on query parameter, locale or page.
 */
export function useRelevantAudience({ page, locale }: { page?: AnyPage; locale: LocalePrefix }) {
  const [audience, setAudience] = useState<Audience>(getDefaultAudience(locale, page));
  const [audienceShouldBeLoaded, setAudienceShouldBeLoaded] = useState(false);
  const [audienceIsLoaded, setAudienceIsLoaded] = useState(false);
  const { project } = useContext(ProjectContext);

  useEffect(() => {
    if (project === Project.Website) {
      setAudienceShouldBeLoaded(true);
    } else {
      setAudience(Audience.Default);
      setAudienceIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Load audience after hydration occurred to avoid difference to server-side generated HTML
    if (!audienceShouldBeLoaded) {
      return;
    }
    let audience: Audience;
    const relevantAudiences = page ? page.audiences : LocaleToAudienceMap[locale];
    const persistedAudienceUrlParam = new URLSearchParams(window.location.search).get('audience') as Audience | null;
    const persistedAudienceCookie = getCookieValue(Cookie.Audience, null) as Audience | null;
    if (persistedAudienceUrlParam && relevantAudiences.includes(persistedAudienceUrlParam)) {
      // User has an audience persisted in URL and it is relevant for this page
      audience = persistedAudienceUrlParam;
    } else if (persistedAudienceCookie && relevantAudiences.includes(persistedAudienceCookie)) {
      // User has an audience persisted in a cookie and it is relevant for this page
      audience = persistedAudienceCookie;
    } else {
      // Simply use an audience of the ones relevant for this page
      audience = getFavoredAudience(relevantAudiences);
    }

    setAudience(audience);
    setAudienceIsLoaded(true);
  }, [page, locale, audienceShouldBeLoaded, audienceIsLoaded]);

  useEffect(() => {
    if (!audience || !audienceIsLoaded) {
      return;
    }
    setCookieValue(Cookie.Audience, audience as string, { expires: 7 });
  }, [audience]);

  return { audience, setAudience, audienceIsLoaded };
}
